<template>
  <v-container fluid class="root">
    <!-- region toolbar -->

    <!-- endregion -->

    <!-- region timetable -->
    <h2>Timetable</h2>
    <div
      class="custom-container-box mt-4"
      style="flex: 1; display: flex; align-items: stretch; flex-direction: column"
    >
      <TimetableHeader
        :searchFilters.sync="searchFilters"
        :clinicsDropdownList="clinicsDropdownList"
        :quickPauseHoliday="quickPauseHoliday"
        :stopBookingLoading="stopBookingLoading"
        @stopBookingChange="handleStopBookingChange"
      >
      </TimetableHeader>

      <v-progress-linear :active="loading" indeterminate absolute bottom />
      <!-- region left col: timetable -->
      <div
        v-if="!searchFilters.selectedClinic"
        class="mt-3 d-flex justify-center align-center"
        style="flex: 1; position: relative"
      >
        <!-- Loading overlay for clinics -->
        <v-overlay :value="loadingClinics" absolute opacity="0.95" color="white">
          <v-card class="pa-8" elevation="0" color="transparent">
            <v-card-text class="text-center">
              <v-progress-circular indeterminate size="80" width="3" color="primary" />
              <h3 class="mt-4 text-h6 font-weight-regular">Loading Clinics</h3>
              <p class="mt-2 text-body-2 grey--text">
                Please wait while we fetch available clinics...
              </p>
            </v-card-text>
          </v-card>
        </v-overlay>

        <transition name="fade">
          <v-card v-if="!loadingClinics" class="pa-8" max-width="500" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="64" color="primary">mdi-hospital-building</v-icon>
              <h2 class="mt-4">Select a Clinic</h2>
              <p class="mt-2 text--secondary">
                Please select a clinic from the dropdown above to view its timetable.
              </p>
            </v-card-text>
          </v-card>
        </transition>
      </div>
      <div v-else style="flex: 1; position: relative">
        <v-sheet
          style="position: absolute; height: 100%; width: 100%; overflow-y: auto"
          elevation="1"
        >
          <!-- Clinic name header spanning all columns -->
          <div v-if="!isClinicStaff && displayedClinics.length > 0" class="clinic-header">
            <span class="text-h6">{{ displayedClinics[0].name | parseAndLocalize('en') }}</span>
          </div>
          <Timetable
            ref="timetable"
            class="timetable"
            v-resize="debouncedDoLayout"
            :date="displayDate"
            :minTime="timeStart"
            :maxTime="timeEnd"
            :gridInterval="{ minutes: bookingSetting.minutesPerSlot }"
            :events="allEvents"
            :columns="displayColumns"
            columnKey="_id"
            showCurrentTime
          >
            <!-- region col/row heads -->
            <!-- current time style (red line)-->
            <!--TODO:should hidden when outside the available time-->
            <template #currentTime="{ now, offsetTop }">
              <div class="currentTime" :style="{ top: offsetTop + 'px' }">
                <label style="left: 0">{{ now | moment('hh:mm') }}</label>
                <label style="right: 0">{{ now | moment('hh:mm') }}</label>
              </div>
            </template>
            <!-- header style (clinics)-->
            <!-- <template #column-head="{ column }">
              <th
                v-if="column.clinic"
                class="col-head"
                :class="{ readonly: column.readonly }"
                :style="{ 'background-color': getCellBackgroundColor(column) }"
              >
                <div class="d-flex align-center pa-2 justify-center">&nbsp;</div>
              </th>
              <th v-else-if="column.nurse" class="col-head">Nurse:{{ column.nurse.name }}</th>
              <th v-else>&nbsp;</th>
            </template> -->
            <template #column-head="{ column }">
              <span />
            </template>
            <!-- time slot style (left column)-->
            <template #row-head="{ time }">
              <td
                class="row-head"
                :class="{
                  halfHourStart: !(+time.start % 1800000),
                  hourStart: !(+time.start % 3600000),
                }"
              >
                <label>{{ time.start | moment('hh:mm') }}</label>
              </td>
            </template>
            <!-- endregion -->
            <!-- region cells -->
            <template #cell-background="{ time, column, cell }">
              <!-- Timetable Empty Cell  -->
              <div
                class="cell-background"
                :class="{
                  readonly: column.readonly,
                  halfHourStart: !(+time.start % 1800000),
                  hourStart: !(+time.start % 3600000),
                }"
                :style="{ 'background-color': getCellBackgroundColor(column) }"
                @click="() => onEmptyCellClick({ cell, time, column })"
              >
                <v-sheet
                  v-if="allowAddBooking"
                  class="addIcon d-flex align-center justify-center"
                  rounded="circle"
                  height="30px"
                  width="30px"
                  color="#CFCFCF"
                  ><v-icon color="#fff">mdi-plus</v-icon></v-sheet
                >
              </div>
            </template>
            <template #cell-event="{ event, cell }">
              <!-- Timetable Event Cell -->
              <div :key="event._id" class="cell-event-container">
                <EventCellCard
                  :event="event"
                  :selected="
                    event._id === selectedTimetable || event._id.split('-')[2] === selectedTimetable
                  "
                  :color="getEventBackgroundColor(event)"
                  :borderColor="getEventBorderColor(event)"
                  @onEventClick="onEventClick(event)"
                  @onRestClick="onRestClick"
                />
              </div>
            </template>
            <!-- endregion -->
            <template #footer>
              <div class="indicators-placeholder"></div>
            </template>
          </Timetable>
        </v-sheet>
      </div>
      <!-- endregion -->
      <!-- region right col: availableClinics -->
      <v-sheet
        v-if="false && isTodayOrFuture && (isCashier || isAdmin)"
        elevation="1"
        class="right-drawer ml-2 d-flex flex-column"
      >
        <div class="flex-grow-1 clickable hover-shade" @click="expandRightDrawer = true" v-ripple>
          <v-btn icon disabled>
            <v-icon> mdi-chevron-left </v-icon>
          </v-btn>
          <div
            class="text-body-2"
            style="
              opacity: 0.3;
              width: 1em;
              white-space: nowrap;
              transform: rotate(90deg);
              transform-origin: left center;
              margin-left: 50%;
            "
          >
            Show Clinic Capacity
          </div>
        </div>
        <v-navigation-drawer
          fixed
          right
          permanent
          :width="RIGHT_COLUMN_WIDTH"
          v-if="expandRightDrawer"
        >
          <template #prepend>
            <div
              class="hover-shade d-flex clickable pa-1"
              v-ripple
              @click="expandRightDrawer = false"
            >
              <div class="text-body-2" style="opacity: 0.3">
                <v-icon>mdi-chevron-right</v-icon>
                Hide
              </div>
            </div>
          </template>
          <v-divider />
          <div class="right-drawer-content">
            <v-list nav dense>
              <v-sheet
                v-if="!availableClinics.length"
                class="text-center grey--text text--lighten-1 my-5"
              >
                &lt; empty &gt;
              </v-sheet>
              <v-list-item
                v-for="m in availableClinics"
                :key="m._id"
                :input-value="true"
                v-bind="clinicOccupancyStyle(m)"
                @click="editMasterCapacity(m)"
                dense
              >
                <v-list-item-avatar v-if="m">
                  <v-img
                    :src="
                      m.avatar ? attnThumbHref(m.avatar, '64') : profile_avatar_placeholder_large
                    "
                  />
                </v-list-item-avatar>
                <v-list-item-content v-if="m">
                  <v-list-item-title class="text-subtitle-1">{{ m.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-subtitle-2">
                    <span>
                      {{ computeCurrentMasterOccupancy(m) }}/{{
                        Number.isFinite(m.servicingCapacity) ? m.servicingCapacity : '∞'
                      }}
                    </span>
                    <span class="resigned" v-if="!m.isActive"> Resigned </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-navigation-drawer>
      </v-sheet>
      <!-- endregion -->
      <!-- region events -->

      <!-- endregion -->
    </div>
    <!-- endregion -->
    <!-- region footer -->
    <v-sheet class="mt-1 legend indicators" v-once>
      <template v-for="indicator in indicators">
        <div class="d-flex align-center">
          <div
            style="
              width: 15px;
              height: 15px;
              margin-right: 5px;
              font-size: 12px;
              border-radius: 4px;
              border: 1px solid;
            "
            :style="{ ...statusStyleSet[indicator.status] }"
          ></div>
          <div>{{ indicator.text }}</div>
        </div>
      </template>
    </v-sheet>
    <v-sheet v-if="false" class="d-flex justify-center">
      <v-btn icon @click="displayColumnPage--" :disabled="displayColumnPage <= 0">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-btn disabled text> {{ displayColumnPage + 1 }} / {{ totalColumnPages }}</v-btn>
      <v-btn
        icon
        @click="displayColumnPage++"
        :disabled="displayColumnPage >= totalColumnPages - 1"
      >
        <v-icon>mdi-arrow-right</v-icon>
      </v-btn>
    </v-sheet>

    <!-- endregion -->

    <!-- region dialogs -->
    <EmptyCellAction
      v-model="showEmptyCellAction"
      :date="displayDate"
      :events="activeEvents"
      v-bind="selectedCell"
      @create="handleEmptyCellCreate"
      @update="updateAvailableMaster"
    />
    <EventCellAction
      v-model="showEventCellAction"
      :selectedDate="displayDate"
      :id="selectedTimetable"
      @cancel="upsertEvent"
      @create="upsertEvent"
      @update="upsertEvent"
      :timeStart="timeStart"
      :timeEnd="timeEnd"
    />
    <RestCellAction v-model="showRestCellAction" :id="selectedTimetable" @cancel="upsertEvent" />
    <!-- endregion -->

    <SelectionDialog
      :items="emptyCellOptions"
      @select="selectOption"
      v-model="showSelectionDialog"
    ></SelectionDialog>
    <BookingDetailsDialog
      :id="selectedBookingId"
      :booking="selectedBookingData"
      :timetableData="selectedTimetableData"
      v-model="showBookingDetailsDialog"
      @create="upsertEventByBooking"
      @update="upsertEventByBooking"
      @cancel="cancelEventByBooking"
      @close="onBookingDetailsDialogClose"
    />
    <TakeRestDialog
      v-model="showTakeRestDialog"
      @close="showTakeRestDialog = false"
      @create="upsertEvent"
      :date="displayDate"
      :time="get(selectedCell, 'time', undefined)"
      :clinic="get(selectedCell, 'column.clinic', undefined)"
    />
    <ClientNotificationListPopup v-model="showSendReminderDialog" />
    <!-- v-model="showEmptyCellAction"
      :date="displayDate"
      :events="activeEvents"
      v-bind="selectedCell"
      @create="upsertEvent"
      @update="updateAvailableMaster" -->
  </v-container>
</template>

<script>
import api from '@/api';
import {
  listAllClinicProfilesIncludingInactive,
  patchProfile,
  ProfileStatus,
} from '@/api/auth/membership';
import { crudCreate, crudList } from '@/api/crud';
import {
  ITimetable,
  listTimeTableByDate,
  readTimeTable,
  readTimeTableByBooking,
} from '@/api/timetable';
import { markCalledCustomerForBookingConfirmation, sendBookingReminder } from '@/api/bookings';
import EmptyCellAction from '@/components/Timetable/EmptyCellAction';
import EventCellAction from '@/components/Timetable/EventCellAction';
import RestCellAction from '@/components/Timetable/RestCellAction';
import TakeRestDialog from '@/components/Timetable/TakeRestDialog.vue';
import TimetableHeader from '@/components/Timetable/TimetableHeader.vue';
import Timetable from '@/components/Timetable/Timetable';
import { attnThumbHref } from '@/services/filters';
import { getOid, oidEquals } from '@/services/utils';
import { flatMap, groupBy, keyBy, sortBy, throttle, get } from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { slotFlagsText } from '@vue/shared';
import BookingDetailsDialog from '@/components/Booking/BookingDetailsDialog.vue';
import EventCellCard from '@/components/Timetable/EventCellCard.vue';
import {
  RESTFUL,
  bookingSetting,
  BookingClinicServicesWithConsultation,
  bookingReplyStatus,
  bookingStatus,
  holidayType,
  holidayReason,
} from '@/data/constants';
import SelectionDialog from '@/components/SelectionDialog.vue';
import { BookingStatus, BookingApprovalStatus } from '@/api/bookings';
import { isStaffOnDuty } from '@/services/rml/appointment';
import ClientNotificationListPopup from '@/components/ClientNotification/ClientNotificationListPopup.vue';
import { parseAndLocalize } from '@/services/filters';

const MIN_COLUMN_WIDTH = 300; // px
const RIGHT_COLUMN_WIDTH = 420; // px
//const EVENTS_COLUMN_WIDTH = 570; // px
const MIN_DISPLAY_COLUMNS = 3; // min columns to display

export default {
  name: 'TimetablePage',
  components: {
    RestCellAction,
    Timetable,
    EmptyCellAction,
    EventCellAction,
    TimetableHeader,
    BookingDetailsDialog,
    EventCellCard,
    SelectionDialog,
    TakeRestDialog,
    ClientNotificationListPopup,
  },
  // props: {
  //   isVoided: Boolean,
  // },
  data() {
    return {
      bookingSetting,
      //region ui state
      showBookingDetailsDialog: false,
      showSelectionDialog: false,
      showTakeRestDialog: false,
      showSendReminderDialog: false,
      tableWidth: 100,
      dateMenu: false,
      displayDate: new Date(),
      loading: false,
      loadingClinics: false,
      displayColumnPage: 0,
      now: new Date(),
      late: false,
      nowInterval: setInterval(() => {
        this.now = new Date();
        if (
          this.selectedTimetable ||
          this.selectedCell ||
          this.showEmptyCellAction ||
          this.showEventCellAction ||
          this.showEventCellAction ||
          this.showBookingDetailsDialog ||
          this.showSelectionDialog ||
          this.showTakeRestDialog ||
          this.showSendReminderDialog
        ) {
        } else {
          this.reload(false);
          this.$el
            .querySelector('.currentTime')
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 60000),
      debouncedDoLayout: throttle(() => this.$nextTick(this.doLayout), 200, { trailing: true }),
      timeStart: { h: 0 },
      timeEnd: { h: 0 },
      showEmptyCellAction: false,
      showEventCellAction: false,
      showRestCellAction: false,
      showDutyMaster: false,
      selectedCell: null,
      selectedTimetable: null,
      selectedBookingId: null,
      selectedBookingData: null,
      selectedTimetableData: null,
      expandRightDrawer: !!this.readState('expandRightDrawer', false),
      showAbsent: !!this.readState('showAbsent', false),
      showVoided: !!this.readState('showVoided', false),
      isVoided: false,

      //endregion

      //region reference data
      /** @type {IClinicProfile[]} */
      availableClinics: [],
      /** @type {ITimetable[]} */
      events: [],
      /** @type {Object|null} quick_pause holiday for selected date and clinic */
      quickPauseHoliday: null,
      stopBookingLoading: false,
      //endregion

      //region derived
      /**
       * column counts of main clinic
       *
       * *Map entries are NOT reactive
       * @type {Map<string, number>}
       */
      clinicColumnCounts: new Map(),

      //TopBar filter
      searchFilters: {
        searchVal: '',
        selectedDate: new Date(),
        selectedClinic: null,
        searchStatus: ['all'],
      },

      emptyCellOptions: [
        {
          text: 'Create Appointment',
          value: () => {
            this.showBookingDetailsDialog = true;
          },
        },
        {
          text: 'Doctor Take Rest',
          value: () => {
            this.showTakeRestDialog = true;
          },
        },
      ],
      eventStatus: {
        approved: 'approved',
        arrived: 'arrived',
        pending: 'pending',
        absent: 'absent',
        completed: 'completed',
        blocked: 'blocked',
      },
    };
  },
  beforeDestroy() {
    clearInterval(this.nowInterval);
  },
  beforeMount() {
    this.loadClinicsAndSetDefault();
    // Check if date query parameter exists and set displayDate
    this.initializeDateFromQuery();
  },
  async mounted() {
    for (let i = +new Date() + 1000 /* timeout 1s */; i > new Date(); ) {
      const $el = this.$el.querySelector('.currentTime');
      if ($el) {
        $el.scrollIntoView({ block: 'center', behavior: 'auto' });
        return;
      } else {
        await new Promise(cb => setTimeout(cb, 100));
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['role', 'profileId', 'isAdmin', 'isClinicStaff', 'clinicId']),
    //region utils
    RIGHT_COLUMN_WIDTH: () => RIGHT_COLUMN_WIDTH,
    //EVENTS_COLUMN_WIDTH: () => EVENTS_COLUMN_WIDTH,
    profile_avatar_placeholder_large() {
      return require('@/assets/Profile_avatar_placeholder_large.png');
    },
    //endregion
    //region ui related
    allowAddBooking() {
      return this.isAdmin || this.isClinicStaff;
    },
    pickerDate: {
      get() {
        return moment(this.displayDate).format('YYYY-MM-DD');
      },
      set(v) {
        this.displayDate = moment(v).toDate();
        this.dateMenu = false;
      },
    },
    isTodayOrFuture() {
      return this.displayDate >= moment().startOf('day');
    },
    //endregion
    //region layout related
    maxDisplayColumns() {
      return Math.max(
        Math.floor((this.tableWidth - 70) /* fixed width row head */ / MIN_COLUMN_WIDTH),
        MIN_DISPLAY_COLUMNS,
      );
    },
    totalColumnPages() {
      return Math.ceil(this.allColumns.length / this.maxDisplayColumns);
    },
    /**
     * controls the final columns shown with pagination
     */
    displayColumns() {
      const displayColumns = this.allColumns
        .slice(
          this.displayColumnPage * this.maxDisplayColumns,
          (this.displayColumnPage + 1) * this.maxDisplayColumns,
        )
        .concat(
          Array.from({ length: this.maxDisplayColumns }, () => ({
            _id: 'placeholder',
            readonly: true,
          })),
        )
        .slice(0, this.maxDisplayColumns);
      return displayColumns;
    },
    //endregion
    //region derived data
    /**
     * controls the final columns shown
     * @type {{
     *   _id: string,
     *   clinic: IMasterProfile,
     *   columnIndex: number,
     *   overflow?: boolean,
     *   readonly?: boolean,
     * }[]}
     */
    allColumns() {
      const { isTodayOrFuture, clinicColumnCounts } = this;
      let clinicsColumns = flatMap(this.displayedClinics, m => {
        const mainColumns = Array.from(
          {
            length: Math.max(
              1, // show at least one column for each clinic
              isTodayOrFuture ? m.servicingCapacity || 0 : 0, // show at least the no. of capacity
              clinicColumnCounts.get(m._id) || 0, // otherwise overflow as needed
            ),
          },
          (_, i) => {
            return {
              /**
               * this key is matched against ITimetable.column._id
               * @see $bookmark$timetableColumnId
               */
              _id: m._id + ':' + i,
              clinic: m,
              columnIndex: i,
              // color: `hsl(${parseInt(m.id.replace(/[a-z]/gi, '')) % 360},80%,30%)`, //random color of clinic for testing
              get overflow() {
                return i >= m._servicingCapacity;
              },
            };
          },
        );
        return mainColumns;
      });

      return clinicsColumns;
    },
    statusStyleSet() {
      return {
        [this.eventStatus.approved]: {
          background: '#DCEFFF',
          borderColor: '#DCEFFF',
        },
        [this.eventStatus.arrived]: {
          background: '#DCF2DE',
          borderColor: '#DCF2DE',
        },
        [this.eventStatus.pending]: {
          background: '#FFF7DC',
          borderColor: '#FFF7DC',
          // color: '#000',
          // fontWeight: 700,
        },
        [this.eventStatus.absent]: {
          background: '#FFC5C5',
          borderColor: '#FFC5C5',
        },
        [this.eventStatus.completed]: {
          background: '#FFFFFF',
          borderColor: '#C4C4C4',
          //fontWeight: 700,
        },
        [this.eventStatus.blocked]: {
          background: '#C4C4C4',
          borderColor: '#C4C4C4',
          // fontWeight: 700,
        },
      };
    },
    indicators() {
      return [
        {
          status: this.eventStatus.approved,
          text: 'Approved',
        },
        {
          status: this.eventStatus.arrived,
          text: 'Arrived',
        },
        {
          status: this.eventStatus.pending,
          text: 'Pending',
        },
        {
          status: this.eventStatus.absent,
          text: 'Absent',
        },
        {
          status: this.eventStatus.completed,
          text: 'Completed',
        },
        {
          status: this.eventStatus.blocked,
          text: 'Blocked',
        },
      ];
    },
    /**
     * controls the final event cells shown
     * @type {ITimetable[]}
     */
    allEvents() {
      // prepend restEvents first so they are rendered underneath
      return this.restEvents.concat(this.allEventsIndependentOfColumns);
    },
    /**
     * @type {ITimetable[]}
     */
    allEventsIndependentOfColumns() {
      const events = this.showAbsent ? this.events : this.filteredEvents;
      return events;
    },
    /**
     * @type {ITimetable[]}
     */
    filteredEvents() {
      const { searchVal, selectedClinic, searchStatus } = this.searchFilters;
      if (searchVal || (searchStatus && searchStatus.length > 0)) {
        //search by tags have intersection
        return this.activeEvents.filter(event => {
          if (event.isRest) return true;
          if (!event.booking) return true;

          //name phone sid

          if (searchVal) {
            const regExp = new RegExp(_.escapeRegExp(searchVal), 'i');
            const { name, phone, sid } = event.booking.created_by;
            if (!regExp.test(name) && !regExp.test(phone) && !regExp.test(sid)) return false;
          }

          // Handle status filter - now supports multiple statuses
          let statusMatches = true;
          if (searchStatus && searchStatus.length > 0 && !searchStatus.includes('all')) {
            // Check if the event status matches any of the selected statuses
            if (
              searchStatus.includes('approved') &&
              event.booking.approvalStatus !== this.eventStatus.approved
            ) {
              statusMatches = false;
            } else if (
              searchStatus.includes('pending') &&
              event.booking.approvalStatus !== this.eventStatus.pending
            ) {
              statusMatches = false;
            } else if (
              searchStatus.includes(bookingStatus.arrived) &&
              event.booking.status !== bookingStatus.arrived
            ) {
              statusMatches = false;
            } else if (
              searchStatus.includes(bookingStatus.absent) &&
              event.booking.status !== bookingStatus.absent
            ) {
              statusMatches = false;
            } else if (
              searchStatus.includes(bookingStatus.completed) &&
              event.booking.status !== bookingStatus.completed
            ) {
              statusMatches = false;
            } else if (
              searchStatus.includes(bookingStatus.cancelled) &&
              event.booking.status !== bookingStatus.cancelled
            ) {
              statusMatches = false;
            }
            // Blocked status handled in "restEvents"
            //  else if (searchStatus.includes('blocked') && !event.isRest) {
            //   statusMatches = false;
            // }
          }

          return statusMatches;
        });
      }

      return this.activeEvents;
    },
    /**
     * @type {ITimetable[]}
     */
    activeEvents() {
      return this.events.filter(ev => ev._status !== '97$cancelled');
    },
    /**
     * @type {ITimetable[]}
     */
    restEvents() {
      const { searchStatus } = this.searchFilters;
      if (!(searchStatus.includes('blocked') || searchStatus.includes('all'))) {
        return [];
      }

      const a = [];
      // Add existing rest events from the events list
      for (const ev of this.events) {
        if (ev.isRest) {
          for (const col of this.allColumns.filter(x => oidEquals(x.clinic, ev.clinic))) {
            if (col.columnIndex > 0) {
              a.push(
                new ITimetable({
                  ...ev,
                  columnIndex: col.columnIndex,
                  _id: `R-${col.columnIndex + 1}-` + ev._id,
                }),
              );
            }
          }
        }
      }

      return a;
    },
    /** @type {Record<string, IMasterProfile>} */
    availableClinicsMap() {
      return keyBy(this.availableClinics, '_id');
    },
    /**
     * compute all related clinics of fetched events
     * note: the returned Set is NOT reactive
     * @type {Set<string>}
     */
    allMasterIDs() {
      const ids = new Set();
      for (const ev of this.events) {
        ev.clinic && ids.add(getOid(ev.clinic));
      }
      return ids;
    },
    /**
     * compute all related clinics of current DISPLAYED events
     * note: the returned Set is NOT reactive
     * @type {Set<string>}
     */
    displayedMasterIDs() {
      const ids = new Set();
      for (const ev of this.allEventsIndependentOfColumns) {
        ev.clinic && ids.add(getOid(ev.clinic));
      }
      return ids;
    },
    activeClinics() {
      return this.availableClinics; //this.availableClinics.filter(x => x.isActive || this.displayedMasterIDs.has(x._id));
    },
    displayedClinics() {
      if (!this.activeClinics.length) return [];

      if (!this.searchFilters.selectedClinic) {
        return [];
      } else {
        // Return only the selected clinic
        const selectedClinic = this.activeClinics.find(
          clinic => clinic._id === this.searchFilters.selectedClinic,
        );
        return selectedClinic ? [selectedClinic] : [];
      }
    },
    //endregion
    clinicsDropdownList() {
      return this.activeClinics.map(clinic => {
        return { value: clinic._id, text: clinic.name };
      });
    },
  },
  watch: {
    // region ui state
    expandRightDrawer(v) {
      this.debouncedDoLayout();
      this.saveState('expandRightDrawer', v);
    },

    showAbsent(v) {
      this.saveState('showAbsent', v);
    },
    showVoided(v) {
      this.saveState('showVoided', v);
    },
    // endregion
    //region side effects
    displayDate: {
      handler() {
        if (this.searchFilters.selectedClinic) {
          this.reload(true);
          // Load all holidays for new date
          this.loadAdHocHolidays();
        }
      },
    },
    // prevent pagination overflow on data changed
    totalColumnPages(v) {
      if (this.displayColumnPage > v - 1) {
        this.displayColumnPage = Math.max(0, v - 1);
      }
    },
    showEventCellAction(v) {
      if (!v) {
        this.selectedTimetable = null;
      }
    },
    showRestCellAction(v) {
      if (!v) {
        this.selectedTimetable = null;
      }
    },
    //endregion
    //region recalculate generated data
    allEventsIndependentOfColumns() {
      this.computeMasterOccupancies();
    },
    //endregion
    'searchFilters.selectedDate': {
      handler(newDate) {
        this.displayDate = newDate;
        // Update URL query parameter to keep it in sync
        this.updateDateInURL(newDate);
      },
      immediate: true,
    },
    'searchFilters.selectedClinic': {
      handler(newVal, oldVal) {
        if (newVal) {
          // Save selected clinic to localStorage
          this.saveState('selectedClinic', newVal);
          // When a clinic is selected, load its specific openHours and reload data
          this.initOpenHoursForClinic(newVal);
          this.reload(true);
          // Load all holidays for selected clinic and date
          this.loadAdHocHolidays();
        } else {
          // Clear the timetable when no clinic is selected
          this.events = [];
          this.quickPauseHoliday = null;
          // Remove saved clinic from localStorage
          this.saveState('selectedClinic', null);
        }
      },
    },
    // Watch for route changes to handle browser navigation
    '$route.query.date': {
      handler(newDate) {
        if (newDate) {
          const queryDate = moment(newDate);
          if (queryDate.isValid()) {
            const newDateObj = queryDate.toDate();
            // Only update if the date is actually different to avoid infinite loops
            if (!moment(this.searchFilters.selectedDate).isSame(queryDate, 'day')) {
              this.searchFilters.selectedDate = newDateObj;
              this.displayDate = newDateObj;
            }
          }
        }
      },
    },
  },
  methods: {
    //region utils
    get,
    moment,
    attnThumbHref,

    /**
     * Initialize date from query parameter on component mount
     */
    initializeDateFromQuery() {
      if (this.$route.query.date) {
        const queryDate = moment(this.$route.query.date);
        if (queryDate.isValid()) {
          this.displayDate = queryDate.toDate();
          this.searchFilters.selectedDate = queryDate.toDate();
        }
      }
    },

    /**
     * Update the URL query parameter when date changes
     * @param {Date} date - The new date to set in the URL
     */
    updateDateInURL(date) {
      if (!date) return;

      const dateString = moment(date).format('YYYY-MM-DD');
      const currentQuery = { ...this.$route.query };

      // Only update URL if the date parameter is different
      if (currentQuery.date !== dateString) {
        this.$router
          .replace({
            name: this.$route.name,
            query: {
              ...currentQuery,
              date: dateString,
            },
          })
          .catch(() => {
            // Ignore navigation duplicated errors
          });
      }
    },
    async loadClinicsAndSetDefault() {
      // Show loading mask while loading clinics
      this.loadingClinics = true;

      try {
        // If user is clinic staff, only load their clinic
        if (this.isClinicStaff && this.clinicId) {
          // Load only the staff's clinic
          let param = {
            filter: { _id: this.clinicId, enabled: true },
          };
          this.availableClinics = (
            await listAllClinicProfilesIncludingInactive(api, param, this.role)
          ).docs;

          // Auto-select the clinic
          this.searchFilters.selectedClinic = this.clinicId;
        } else {
          // Load all available clinics for admin users
          let param = {
            sort: { name: 1 },
            limit: -1,
            filter: { enabled: true },
          };
          this.availableClinics = (
            await listAllClinicProfilesIncludingInactive(api, param, this.role)
          ).docs;

          // Try to load saved clinic from localStorage
          const savedClinicId = this.readState('selectedClinic');

          if (savedClinicId && this.availableClinics.some(c => c._id === savedClinicId)) {
            // Use saved clinic if it still exists
            this.searchFilters.selectedClinic = savedClinicId;
          } else if (this.isClinicStaff) {
            // If user is master, try to set their clinic
            const masterClinic = this.availableClinics.find(clinic => clinic.id === this.profileId);
            if (masterClinic) {
              this.searchFilters.selectedClinic = masterClinic._id;
            }
          } else if (this.availableClinics.length > 0) {
            // Otherwise, select the first available clinic as default
            this.searchFilters.selectedClinic = this.availableClinics[0]._id;
          }
        }
      } finally {
        this.loadingClinics = false;
      }
    },
    async initOpenHoursForClinic(clinicId) {
      // Find the selected clinic
      const clinic = this.availableClinics.find(c => c._id === clinicId);
      if (clinic && clinic.openHours && clinic.openHours.length > 0) {
        // Get min and max times from clinic's openHours
        const minStart = Math.min(...clinic.openHours.map(h => h.start));
        const maxEnd = Math.max(...clinic.openHours.map(h => h.end));

        const start = moment().startOf('day').add(minStart, 'minutes');
        const end = moment().startOf('day').add(maxEnd, 'minutes');
        this.timeStart = { h: start.hours(), m: start.minutes() };
        this.timeEnd = { h: end.hours(), m: end.minutes() };
      } else {
        // Fallback to default hours if clinic doesn't have openHours
        this.timeStart = { h: 7, m: 0 };
        this.timeEnd = { h: 20, m: 0 };
      }
    },
    async addRestTime() {
      await Promise.all(
        this.availableClinics.map(async x => {
          if (x.isActive) {
            await crudCreate(api, 'rml/admin/timetables/takeRest', {
              clinic: getOid(x),
              time: {
                start: +moment(this.displayDate).startOf('day').add(10, 'h'),
                end: +moment(this.displayDate).startOf('day').add(10.5, 'h'),
              },
            });
            await crudCreate(api, 'rml/admin/timetables/takeRest', {
              clinic: getOid(x),
              time: {
                start: +moment(this.displayDate).startOf('day').add(20, 'h'),
                end: +moment(this.displayDate).startOf('day').add(20.5, 'h'),
              },
            });
          }
        }),
      );
      await this.reload(false);
    },
    saveState(key, v = undefined) {
      if (arguments.length < 2) v = this[key];
      const k = 'ui.timetable.' + key;
      localStorage.setItem(k, JSON.stringify(v));
    },
    readState(key, def = undefined) {
      const k = 'ui.timetable.' + key;
      try {
        const v = localStorage.getItem(k);
        return v == null ? def : JSON.parse(v);
      } catch (_) {
        return def;
      }
    },
    //endregion
    //region timetable layout
    doLayout() {
      if (this.$refs.timetable?.$el?.clientWidth) {
        this.tableWidth = this.$refs.timetable.$el.clientWidth;
      }
    },
    //endregion
    //region styling
    getCellBackgroundColor(column) {
      return column.overflow
        ? // if cell is overflow, mark red
          'rgba(128,0,0,0.1)'
        : column.readonly
        ? // grey out readonly and placeholder cells
          'rgba(0,0,0,0.1)'
        : null;
    },
    getEventStatus(ev) {
      if (!ev.booking) return;

      // First check approval status
      // pending
      if (ev.booking.approvalStatus == BookingApprovalStatus.pending) {
        return this.eventStatus.pending;
      }

      // Then check booking status for approved bookings
      // arrived
      if (ev.booking.status === BookingStatus.arrived) {
        return this.eventStatus.arrived;
      }

      // approved (but not yet arrived)
      if (
        ev.booking.status === BookingStatus.created &&
        ev.booking.approvalStatus == BookingApprovalStatus.approved
      ) {
        return this.eventStatus.approved;
      }

      // absent
      if (ev.booking.status === BookingStatus.absent) {
        return this.eventStatus.absent;
      }

      // completed
      if (ev.booking.status === BookingStatus.completed) {
        return this.eventStatus.completed;
      }

      // cancelled
      if (ev.booking.status === BookingStatus.cancelled) {
        return this.eventStatus.blocked;
      }

      // default/blocked
      return this.eventStatus.blocked;
    },
    getEventBackgroundColor(ev) {
      const status = this.getEventStatus(ev);
      if (!status) return;
      return this.statusStyleSet[status].background;
    },
    getEventBorderColor(ev) {
      const status = this.getEventStatus(ev);
      if (!status) return;
      return this.statusStyleSet[status].borderColor;
    },

    clinicOccupancyStyle(m) {
      const clinicOccupancy = this.computeCurrentMasterOccupancy(m);
      const cap = m.servicingCapacity || Number.POSITIVE_INFINITY;
      return {
        color: clinicOccupancy >= cap ? 'error' : clinicOccupancy >= cap / 2 ? 'warning' : 'green',
      };
    },
    // getSeat(s) {
    //   const chars = ['0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
    //   return s && (chars[s] || s.replace(/[0-9]/g, x => chars[x]));
    // },
    //endregion
    //region derived data
    /**
     * when allEvents changed, call this to assign event to columns
     * *Note: this call depends on {@link #availableClinics}. Make sure you fetch the
     *        clinic profiles first
     */
    computeMasterOccupancies() {
      const map = new Map();
      const assMap = new Map();
      /** @type {Record<string, ITimetable[]>} */
      const clinics = groupBy(this.allEventsIndependentOfColumns, ev => {
        return getOid(ev.clinicColumnId);
      });

      for (const [mid, events] of Object.entries(clinics)) {
        // short circuit
        if (events.length === 0) {
          continue;
        }
        /**
         * @param {ITimetable[]} events
         * @return {number} column count
         */
        const scanEvents = events => {
          // short circuit
          if (!events.length) {
            return 0;
          } else if (events.length === 1) {
            events[0].columnIndex = 0;
            return 1;
          }
          const bitmap = [];
          /** @type {[Date, boolean, ITimetable][]} */
          const startEndList = sortBy(
            events.reduce((acc, ev) => {
              acc.push([+ev.time.start, /*isStart*/ true, ev]);
              acc.push([ev.time.end - 1 /*end time exclusive*/, false, ev]);
              return acc;
            }, []),
            tuple => tuple[0 /* Date */],
          );
          for (const [, isStart, ev] of startEndList) {
            // start
            if (isStart) {
              let firstEmptyIdx = bitmap.findIndex(x => !x);
              if (firstEmptyIdx < 0) {
                firstEmptyIdx = bitmap.length;
              }
              ev.columnIndex = firstEmptyIdx;
              bitmap[firstEmptyIdx] = true;
            }
            // end
            else {
              bitmap[ev.columnIndex] = false;
            }
          }
          return bitmap.length;
        };

        const mainColumns = scanEvents(events);
        map.set(mid, mainColumns);
      }

      this.clinicColumnCounts = map;
      return map;
    },
    computeCurrentMasterOccupancy(m) {
      const now = this.now;
      return this.events.filter(
        ev =>
          ev._status !== '97$cancelled' &&
          ev._status !== '98$absent' &&
          oidEquals(ev.clinic, m) &&
          ev.time.contains(now),
      ).length;
    },
    //endregion
    //region event handling
    async reload(refresh = true) {
      // Only reload if a clinic is selected
      if (!this.searchFilters.selectedClinic) {
        this.events = [];
        return;
      }

      try {
        this.loading = true;
        if (refresh) {
          this.events = [];
        }

        const timeStart = moment(this.displayDate).startOf('day').valueOf();
        const timeEnd = moment(this.displayDate).endOf('day').valueOf();

        // Fetch timetable data for the selected clinic only
        const allEvents = await listTimeTableByDate(api, {
          timeStart,
          timeEnd,
          clinic: this.searchFilters.selectedClinic,
        });

        // Filter events for the selected clinic
        this.events = allEvents.filter(event => {
          // Handle both string clinic IDs and populated clinic objects
          const eventClinicId =
            typeof event.clinic === 'string' ? event.clinic : getOid(event.clinic);
          return eventClinicId === this.searchFilters.selectedClinic;
        });
      } finally {
        this.loading = false;
      }
    },
    handleEmptyCellCreate(result) {
      // Check if result is a booking or a rest/timetable event
      // Bookings should have booking-specific fields, while rest entries have isRest or different structure
      if (result && (result.guest || result.serviceDetails || result.approvalStatus)) {
        // It's a booking - use upsertEventByBooking
        this.upsertEventByBooking(result);
      } else {
        // It's a rest/timetable event - use upsertEvent
        this.upsertEvent(result);
      }
    },
    async upsertEvent(ev) {
      const id = getOid(ev);
      let x;
      try {
        x = await readTimeTable(api, id);
      } catch (e) {
        return this.removeEvent(ev);
      }
      if (x === undefined) return this.removeEvent(ev);
      //check created/updated time is same day as display day
      if (+moment(x.time.start).startOf('day') !== +moment(this.displayDate).startOf('day')) {
        return this.removeEvent(ev);
      }
      const i = this.events.findIndex(x => x._id === id);
      i >= 0 ? this.$set(this.events, i, x) : this.events.push(x);
    },
    async upsertEventByBooking(booking) {
      this.updateAvailableMaster();
      const id = getOid(booking);
      let x;
      try {
        x = await readTimeTableByBooking(api, id);
      } catch (e) {
        return this.removeEventFromBooking(booking);
      }
      if (x === undefined) return this.removeEventFromBooking(booking);
      //check created/updated time is same day as display day
      if (+moment(x.time.start).startOf('day') !== +moment(this.displayDate).startOf('day')) {
        return this.removeEventFromBooking(booking);
      }
      const i = this.events.findIndex(x => x.booking?._id === id);
      i >= 0 ? this.$set(this.events, i, x) : this.events.push(x);
      // // Get the booking ID - handle both _id and id properties
      // const bookingId = booking._id || booking.id;

      // // Find the existing timetable event that contains this booking
      // const eventIndex = this.events.findIndex(event => {
      //   const eventBookingId = event.booking?._id || event.booking?.id;
      //   return eventBookingId === bookingId;
      // });

      // if (eventIndex >= 0) {
      //   // Update the existing event's booking data
      //   const existingEvent = this.events[eventIndex];

      //   // Preserve the booking's ID format from the existing event
      //   const updatedBooking = {
      //     ...booking,
      //     _id: booking._id || booking.id,
      //     id: booking.id || booking._id
      //   };

      //   // Update the event with the new booking data
      //   // Also update _status if booking status changed to cancelled
      //   const updatedEvent = {
      //     ...existingEvent,
      //     booking: updatedBooking,
      //     // Update the timetable's _status to match booking status if cancelled
      //     _status: booking.status === '97$cancelled' ? '97$cancelled' : existingEvent._status
      //   };

      //   // Check if the booking is still on the same day
      //   if (+moment(existingEvent.time.start).startOf('day') !== +moment(this.displayDate).startOf('day')) {
      //     return this.removeEventFromBooking(booking);
      //   }

      //   // Update the event in place
      //   this.$set(this.events, eventIndex, updatedEvent);
      // } else {
      //   // If no existing event found, try to fetch the timetable
      //   let x;
      //   try {
      //     x = await readTimeTableByBooking(api, bookingId);
      //   } catch (e) {
      //     console.error('Failed to fetch timetable by booking:', e);
      //     return this.removeEventFromBooking(booking);
      //   }
      //   if (x === undefined) return this.removeEventFromBooking(booking);

      //   // Check created/updated time is same day as display day
      //   if (+moment(x.time.start).startOf('day') !== +moment(this.displayDate).startOf('day')) {
      //     return this.removeEventFromBooking(booking);
      //   }

      //   this.events.push(x);
      // }
    },
    cancelEventByBooking(booking) {
      this.updateAvailableMaster();
      this.$refs.appointmentDrawer?.upsert(booking);
      return this.removeEventFromBooking(booking);
    },

    removeEvent(ev) {
      const id = getOid(ev);
      this.events = this.events.filter(x => x._id !== id);
    },
    removeEventFromBooking(booking) {
      const bookingId = booking._id || booking.id;
      this.events = this.events.filter(x => {
        const eventBookingId = x.booking?._id || x.booking?.id;
        return eventBookingId !== bookingId;
      });
    },
    //endregion
    //region click listeners
    onEmptyCellClick({ cell, time, column }) {
      if (!this.allowAddBooking) return;
      if (column.clinic) {
        // cell of overloaded capacity is readonly
        if (!column.readonly) {
          this.selectedCell = { time, column };
          this.selectedBookingId = null;
          this.selectedBookingData = null;
          this.selectedTimetableData = null;
          this.showEmptyCellAction = true;
        } else {
          // ignore
        }
      }
      if (column.nurse) {
        //for testing custom column
        alert('You click the nurse:' + column.nurse.name);
        this.selectedCell = { time, column };
        this.showEmptyCellAction = true;
      }
    },
    onEventClick(event) {
      // Find the complete timetable data for this event
      const timetableEvent = this.events.find(
        e => e._id === event._id || e.booking?._id === event._id,
      );

      if (
        event.booking &&
        BookingClinicServicesWithConsultation.includes(event.booking.clinicService) &&
        (this.isClinicStaff || this.isDispenser)
      ) {
        const clientId = event.booking.created_by.id;
        const bookingId = event.booking.id;
        this.$router.push({
          name: 'ConsultationOverview',
          params: { id: clientId },
          query: { booking: bookingId },
        });
      } else {
        // Pass both booking and timetable data to the dialog
        this.selectedBookingId = event.booking?.id || event._id;
        this.selectedBookingData = event.booking || event;
        this.selectedTimetableData = timetableEvent || event;
        this.showBookingDetailsDialog = true;
      }
    },
    async onRestClick(id) {
      this.selectedTimetable = id;
      this.showRestCellAction = true;
    },

    async editMasterCapacity(m) {
      try {
        let cap = prompt(
          'Enter Servicing Capacity:',
          m.servicingCapacity == null ? -1 : m.servicingCapacity,
        );
        if (!cap) return;
        cap = parseInt(cap, 10);
        if (!Number.isFinite(cap)) return;
        await patchProfile(api, m._id, { servicingCapacity: cap >= 0 ? cap : null });
        await this.updateAvailableMaster();
      } catch (e) {
        alert(e);
      }
    },
    /**
     * Update available master data if needed
     */
    async updateAvailableMaster() {
      // Clinics are already loaded in loadClinics(), no need to reload here
      // This method is kept for backward compatibility
    },
    //endregion

    selectOption(item) {
      this.showSelectionDialog = false;
      if (typeof item.value === 'function') {
        item.value();
      }
    },

    onBookingDetailsDialogClose() {
      // Clear selected booking data when dialog closes
      this.selectedBookingId = null;
      this.selectedBookingData = null;
      this.selectedTimetableData = null;
      this.showBookingDetailsDialog = false;
    },

    async loadAdHocHolidays() {
      if (!this.searchFilters.selectedClinic || !this.displayDate) {
        this.quickPauseHoliday = null;
        return;
      }

      try {
        this.stopBookingLoading = true;

        const startOfDay = moment(this.displayDate).startOf('day').toISOString();
        const endOfDay = moment(this.displayDate).endOf('day').toISOString();

        // Load all holidays for the clinic and date
        const response = await crudList(api, RESTFUL.holidays.list, {
          filter: {
            clinic: this.searchFilters.selectedClinic,
            type: holidayType.clinic,
            'time.start': { $lte: endOfDay },
            'time.end': { $gte: startOfDay },
            reason: holidayReason.adhoc,
          },
          limit: -1,
        });

        const holidays = response.docs || [];
        this.quickPauseHoliday = holidays.length > 0 ? holidays[0] : null;
      } catch (error) {
        console.error('Error loading holidays:', error);
        this.quickPauseHoliday = null;
      } finally {
        this.stopBookingLoading = false;
      }
    },

    async handleStopBookingChange({ stopBooking, date, clinic, holidayId }) {
      try {
        this.stopBookingLoading = true;

        if (stopBooking) {
          const payload = {
            name: JSON.stringify({
              en: 'Booking Service Stop',
              'zh-hk': '暫停預約',
              'zh-cn': '暂停预约',
            }),
            time: {
              start: moment(date).startOf('day').toISOString(),
              end: moment(date).endOf('day').toISOString(),
            },
            clinic: clinic,
            type: holidayType.clinic,
            reason: holidayReason.adhoc,
          };

          await api.post(RESTFUL.holidays.list, payload);

          this.$store.dispatch('alert/updateAlertMessage', {
            msg: 'Booking stopped for selected date',
            type: 'success',
            color: 'success',
          });
        } else {
          if (holidayId) {
            await api.delete(`${RESTFUL.holidays.delete}/${holidayId}`);

            this.$store.dispatch('alert/updateAlertMessage', {
              msg: 'Booking enabled for selected date',
              type: 'success',
              color: 'success',
            });
          }
        }

        // Reload all holidays
        await this.loadAdHocHolidays();

        // Reload timetable data
        await this.reload(true);
      } catch (error) {
        console.error('Error toggling stop booking:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to update booking status',
          type: 'error',
          color: 'error',
        });

        // Revert the switch on error
        await this.loadAdHocHolidays();
      } finally {
        this.stopBookingLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.root {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0px 24px;
  //region timetable
  .timetable {
    font-size: 10px;
    margin-top: 10px;
  }
  .show-sid {
    font-size: 8px;
  }
  .assistOf {
    font-size: 8px;
    font-weight: bold;
  }

  ::v-deep .v-toolbar {
    flex: 0;

    .v-input {
      margin-left: 2px;
      margin-right: 2px;
    }
  }

  // time column has fixed width
  ::v-deep .timetable .col-head.row-head {
    width: 70px;
  }
  .clinic-header {
    position: sticky;
    top: 0;
    z-index: 4;
    background-color: #fff;
    padding: 4px;
    text-align: center;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .col-head {
    //added by Jacky for testing
    position: sticky;
    top: 48px; // Adjust for clinic header height
    backdrop-filter: blur(4px);
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 3;
  }

  .row-head {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80px;
    //border-bottom: 1px solid silver;
    position: relative;
    label {
      font-size: 14px;
      line-height: 28px;
      display: inline-block;
      position: absolute;
      top: -14px;
      z-index: 2;
    }
    &.halfHourStart {
      //border-top: 1px solid var(--v-primary-lighten5);
    }
    &.hourStart {
      //border-top: 2px solid var(--v-primary-lighten2);
    }
  }

  .currentTime {
    position: absolute;
    left: 0;
    right: 0;
    border-top: 2px solid red;
    pointer-events: none;
    z-index: 2;

    label {
      background-color: red;
      color: white;
      position: absolute;
      bottom: 0;
      padding: 0.1em 0.5em;
      border-radius: 8px 8px 0 0;
    }
  }

  //endregion

  .legend {
    font-size: 12px;

    span {
      padding: 0 2px;
      margin: 0 2px;
    }
  }
}

//region cell
.cell {
  &-background {
    height: 80px;
    border-right: 1px solid var(--v-muted-base);
    border-bottom: 1px solid var(--v-muted-base);
    overflow: hidden;
    font-size: 6px;
    color: var(--v-primary-base);
    display: flex;
    align-items: center;
    justify-content: center;
    .addIcon {
      transition: 0.2s;
      opacity: 0;
    }
    &:hover:not(.readonly) {
      // box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
      .addIcon {
        opacity: 1;
      }
    }
    &.halfHourStart {
      border-top: 1px solid var(--v-primary-lighten5);
    }
    &.hourStart {
      border-top: 2px solid var(--v-primary-lighten2);
    }
  }

  &-event {
    &-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      > * {
        pointer-events: auto;
      }
    }

    flex: 1;
    overflow: auto;
    text-align: center;

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }

    .text-subtitle-1 {
      font-size: 9px !important;
      line-height: 1.5em;
    }
    .display-4 {
      font-size: 14px !important;
      font-weight: 700;
      line-height: 1em;
    }
    .text-seat {
      font-size: 14px;
      line-height: 1.2em;
    }
    .text-sid {
      font-size: 6px;
      font-weight: 700;
      line-height: 1.6em;
      // margin-top: 4px;
      // margin-bottom: 4px;
    }

    &.selected {
      outline: 4px solid red !important;
    }
  }
}
//endregion

//region right-drawer
.right-drawer {
  .hover-shade:hover,
  .hover-shade:active {
    background-color: rgba(0, 0, 0, 0.05);
  }
  ::v-deep .v-navigation-drawer__content {
    position: relative;
  }
  ::v-deep .v-list-item__avatar {
    margin-top: 6px !important;
    margin-bottom: 6px !important;
  }
  &-content {
    position: absolute;
    top: 1px; // v-divider
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
  }
  .resigned {
    color: var(--v-error-base);
  }
}
//endregion
.indicators {
  // position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 15px;
  .cell {
    border: 2px solid;
  }
  background: transparent;
  padding: 10px 0;
}

// Fade transition for smooth loading
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
